import { DecisionGraph } from "@imminently/decisively-core";
import { refireSession, sessionService } from "@/app/providers/services";
import { useFullRelease } from "@/common/hooks_useFullRelease";
import { useNotify } from "@/common/notifications";
import { useScope } from "@/redux/scope";
import { MonitorTableToolbar } from "@/ui/blocks/MonitorTableToolbar";
import { ReleaseFilterSelect, useReleaseFilter } from "@/ui/blocks/ReleaseFilterSelect";
import styled from "@emotion/styled";
import AttributeValue from "@imminently/decisively-ui/AttributeValue";
import Dialog from "@imminently/decisively-ui/Dialog";
import { DialogControls } from "@/ui/dialogs/DialogHeader";
import Button from "@imminently/decisively-ui/Button";
import Chip from "@imminently/decisively-ui/Chip";
import DataTable, { type DataTableColumn } from "@imminently/decisively-ui/DataTable";
import DetailTable from "@imminently/decisively-ui/DetailTable";
import Icon from "@imminently/decisively-ui/Icon";
import Select, { type SelectOption } from "@imminently/decisively-ui/Select";
import TextInput from "@imminently/decisively-ui/TextInput";
import Tooltip from "@imminently/decisively-ui/Tooltip";
import type { QueryParams } from "@imminently/immi-query";
import { Typography, type TypographyProps, useTheme } from "@mui/material";
import { find } from "lodash";
import moment from "moment";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { InteractionTable } from "./InteractionTable";
import { ShortID } from "./utils";
import { fetchRelease } from "@/features/release/fetchRelease";

const releaseGraphCache = new Map<string, any>();
const releaseGraphRequests = new Map<string, Promise<any>>();
const DETAIL_PANEL_TOGGLE_FIELD = "__detail_panel_toggle__";

const TableContainer = styled.div<{ $showFailedState?: boolean }>`
  display: flex;
  flex-direction: column;
  height: 100%;

  /* > div {
    flex: 1;
    border-radius: 0;
    box-shadow: none;
    display: flex;
    flex-direction: column;
    overflow: auto;
  } */

  .MuiToolbar-root {
    position: sticky;
    top: 0;
    z-index: 1;
    background-color: var(--background);
    padding-left: 2rem;
    padding-right: 1rem;

    [class*="MTableToolbar-title"] > .MuiTypography-root {
      font-size: 1.125rem;
      font-style: normal;
      font-weight: 600;
      line-height: 1.25rem;
      text-transform: none;
    }

    [class*="MTableToolbar-searchField"] {
      order: 2;
    }

    [class*="MTableToolbar-actions"] {
      order: 1;
    }
  }

  .MuiTableRow-root {
    border-top: 1px solid #e5e5e5;
  }

  .MuiTableRow-head {
    .MuiTableCell-head {
      padding: 0.5rem 0;
      font-weight: 600;
      font-size: 0.75rem;
      line-height: 200%;
      ${(p) => (p.$showFailedState ? "padding-left: 1rem;" : "")}
    }
  }

  .MuiTableBody-root .MuiTableCell-root:not(.MuiTableCell-paddingNone) {
    padding: 0.75rem 0;
  }

  .MuiTableCell-root {
    padding: 0;
    border: none;
  }

  .MuiTableCell-root .MuiCollapse-root .MuiPaper-root {
    box-shadow: none;
    background-color: var(--subtle-light);
    border-radius: 0;
    padding: 1rem;

    table {
      border-collapse: separate; // required for border-radius to work
      border-spacing: 0 0.25rem; // space between rows
    }

    .MuiTableRow-root {
      border-top: none;

      > .MuiTableCell-root:first-child {
        padding-left: 1rem !important;
      }
    }

    .MuiTableHead-root > .MuiTableRow-root {
      > .MuiTableCell-head {
        padding: 0;
        background: none;
      }
    }

    .MuiTableBody-root > .MuiTableRow-root {
      background-color: var(--background);

      > td.MuiTableCell-body {
        padding: 0.5rem 0;
        border: 1px solid var(--border);
        border-right: none;
        border-left: none;

        &:first-child {
          border-top-left-radius: 0.5rem;
          border-bottom-left-radius: 0.5rem;
          border-left: 1px solid var(--border);
        }

        &:last-child {
          border-top-right-radius: 0.5rem;
          border-bottom-right-radius: 0.5rem;
          border-right: 1px solid var(--border);
        }
      }
    }
  }
`;

const StatusCell = ({ value, color }: { value?: string; color: string | ((val: string) => string) }) => {
  const { t } = useTranslation();
  const label = value ?? "in-progress";
  const variant = label === "in-progress" ? "info" : label === "complete" ? "success" : undefined;
  return (
    <Chip
      variant={variant}
      backgroundColor={variant ? undefined : typeof color === "string" ? color : color(label)}
    >
      {t(`tasks.status.${label}`)}
    </Chip>
  );
};

const TruncatedField = ({ value, className, style, ...props }: { value: string } & TypographyProps) => (
  <Tooltip placement="right">
    <Tooltip.Trigger asChild>
      <Typography
        {...props}
        className={`block min-w-0 max-w-full overflow-hidden text-ellipsis whitespace-nowrap ${className ?? ""}`}
        style={{ width: "100%", ...style }}
      >
        {value}
      </Typography>
    </Tooltip.Trigger>
    <Tooltip.Content>{value}</Tooltip.Content>
  </Tooltip>
);

const MissingGoalField = ({ goal }: { goal: string }) => (
  <Tooltip placement="right">
    <Tooltip.Trigger asChild>
      <Typography className="block min-w-0 max-w-full overflow-hidden text-ellipsis whitespace-nowrap">
        No match
      </Typography>
    </Tooltip.Trigger>
    <Tooltip.Content>{goal}</Tooltip.Content>
  </Tooltip>
);

export type SessionTableProps = {
  filter?: QueryParams;
  onSessionClick: (session: any) => void;
  releaseFilterKey?: string;
  /** When true, show columns for failed-interactions view (Last Failed, Interactions failed/total/complete, Refire All Failed). */
  showFailedState?: boolean;
};

type IndexRow = { indexKey: string; value: string };
type IndexValue = string | number | boolean | null;
type NormalizedIndexEntry = [string, IndexValue | IndexValue[]];

const isRenderableIndexValue = (value: unknown): value is IndexValue =>
  value === null || ["string", "number", "boolean"].includes(typeof value);

const normalizeIndexEntry = ([key, value]: [string, unknown]): NormalizedIndexEntry | null => {
  if (Array.isArray(value)) {
    const values = value.filter(isRenderableIndexValue);
    return values.length ? [key, values] : null;
  }

  if (isRenderableIndexValue(value)) return [key, value];

  return null;
};

const formatIndexValue = (value: IndexValue | IndexValue[]) =>
  (Array.isArray(value) ? value : [value]).map((item) => String(item)).join(", ");

const renderIndexValue = (value: IndexValue | IndexValue[]) =>
  (Array.isArray(value) ? value : [value]).map((item, index) => (
    <React.Fragment key={index}>
      {index > 0 ? ", " : null}
      <AttributeValue node={{ derived: item }} />
    </React.Fragment>
  ));

const IndicesCell = ({ indices }: { indices?: Record<string, unknown> }) => {
  const entries = Object.entries(indices || {})
    .map(normalizeIndexEntry)
    .filter(Boolean) as NormalizedIndexEntry[];

  if (entries.length === 0) return null;

  return (
    <div className="flex items-center gap-2">
      {entries.slice(0, 2).map(([key, value]) => (
        <Chip
          key={key}
          size="sm"
        >
          <Chip.Label>{`${key}: ${formatIndexValue(value)}`}</Chip.Label>
        </Chip>
      ))}
      {entries.length > 2 && (
        <Tooltip
          theme="light"
          placement="bottom-start"
          interactable={true}
        >
          <Tooltip.Trigger asChild>
            <span onClick={(event) => event.stopPropagation()}>
              <Chip
                size="sm"
                style={{ cursor: "pointer" }}
              >
                <Chip.Label>{`+${entries.length - 2}`}</Chip.Label>
              </Chip>
            </span>
          </Tooltip.Trigger>
          <Tooltip.Content asChild={true}>
            <div className=" min-w-[240px] max-w-[unset]">
              <DetailTable>
                {entries.map(([key, value]) => (
                  <DetailTable.Item key={key}>
                    <DetailTable.Title>{key}</DetailTable.Title>
                    <DetailTable.Content>{renderIndexValue(value)}</DetailTable.Content>
                  </DetailTable.Item>
                ))}
              </DetailTable>
            </div>
          </Tooltip.Content>
        </Tooltip>
      )}
    </div>
  );
};

export const SessionTable = ({
  filter,
  onSessionClick,
  releaseFilterKey,
  showFailedState = false,
}: SessionTableProps) => {
  const theme = useTheme();
  const { project } = useScope();
  const currentRelease = useFullRelease();
  const { releaseId: releaseFilter, setReleaseId: setReleaseFilter } = useReleaseFilter(releaseFilterKey);
  const [filteredRelease, setFilteredRelease] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [advancedIndices, setAdvancedIndices] = useState<Record<string, string> | null>(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [advancedDraft, setAdvancedDraft] = useState<IndexRow[]>([]);
  const [refiringId, setRefiringId] = useState<string | null>(null);
  const [expandedSessionRowIds, setExpandedSessionRowIds] = useState<Set<any>>(() => new Set());
  const notify = useNotify();
  const notifyRef = React.useRef(notify);
  const release = releaseFilter ? filteredRelease : currentRelease;

  React.useEffect(() => {
    notifyRef.current = notify;
  }, [notify]);

  React.useEffect(() => {
    if (currentRelease?.id) {
      releaseGraphCache.set(currentRelease.id, currentRelease);
    }
  }, [currentRelease]);

  React.useEffect(() => {
    if (!releaseFilter) {
      setFilteredRelease(null);
      return;
    }

    const cachedRelease = releaseGraphCache.get(releaseFilter);
    if (cachedRelease) {
      setFilteredRelease(cachedRelease);
      return;
    }

    setFilteredRelease(null);
    let cancelled = false;
    let request = releaseGraphRequests.get(releaseFilter);
    if (!request) {
      request = fetchRelease(releaseFilter);
      releaseGraphRequests.set(releaseFilter, request);
    }

    request
      .then((nextRelease) => {
        releaseGraphCache.set(releaseFilter, nextRelease);
        releaseGraphRequests.delete(releaseFilter);
        if (!cancelled) setFilteredRelease(nextRelease);
      })
      .catch((error) => {
        releaseGraphRequests.delete(releaseFilter);
        if (!cancelled) notifyRef.current.error(error?.message ?? "Failed to load release");
      });

    return () => {
      cancelled = true;
    };
  }, [releaseFilter]);

  const availableIndexOptions = useMemo((): SelectOption[] => {
    if (!release?.rule_graph) return [];
    const graph = DecisionGraph.from(release.rule_graph);
    return graph.ids()
      .map((id) => (graph.field(id, "entity") === "global" ? graph.field(id, "publicId") as string | undefined : undefined))
      .filter((publicId): publicId is string => Boolean(publicId))
      .map((publicId) => ({ value: publicId, label: publicId }));
  }, [release?.rule_graph]);

  const fullFilter = useMemo(() => {
    const defaultBase = {
      model: project,
      ...(releaseFilter ? { release: releaseFilter } : {}),
      sort: { field: "lastModified", order: "desc" } as const,
    };
    const base = filter ? { ...defaultBase, ...filter } : { ...defaultBase, id: `*${searchTerm}*` || undefined };
    if (advancedIndices !== null) {
      const { id: _id, ...rest } = base as Record<string, unknown>;
      const entries = Object.entries(advancedIndices).map(([k, v]) => [`indices.${k}`, v] as const);
      return { ...rest, ...Object.fromEntries(entries) };
    }
    return base;
  }, [filter, project, releaseFilter, advancedIndices, searchTerm]);

  const {
    data: sessions,
    total,
    query,
    pagination,
  } = sessionService.useResources({
    pagination: { page: 0, perPage: 20 },
    filter: fullFilter,
    refetchOnWindowFocus: false,
  });
  const [lastAvailablePage, setLastAvailablePage] = useState<number | null>(null);
  const lastEmptyPageNotificationRef = React.useRef<number | null>(null);
  const [sessionsAutosizeKey, setSessionsAutosizeKey] = useState(0);
  const lastSessionsFingerprintRef = React.useRef<string>("");
  const showReleaseColumn = !releaseFilter;

  React.useEffect(() => {
    setLastAvailablePage(null);
    lastEmptyPageNotificationRef.current = null;
  }, [fullFilter, pagination.pagination.perPage]);

  React.useEffect(() => {
    const fingerprint = (sessions || []).map((s: any) => String(s?.id ?? s?._id ?? "")).join("|");
    if (fingerprint !== lastSessionsFingerprintRef.current) {
      lastSessionsFingerprintRef.current = fingerprint;
      setSessionsAutosizeKey((k) => k + 1);
    }
  }, [sessions]);

  React.useEffect(() => {
    const currentPage = pagination.pagination.page;

    if (currentPage === 0 || query.isFetching || !query.isFetched || sessions.length > 0) return;

    const lastPage = currentPage - 1;
    setLastAvailablePage(lastPage);

    if (lastEmptyPageNotificationRef.current !== currentPage) {
      notify.info("No more pages");
      lastEmptyPageNotificationRef.current = currentPage;
    }
  }, [notify, pagination.pagination.page, query.isFetched, query.isFetching, sessions.length]);

  const handleRefireAll = useCallback(
    async (e: React.MouseEvent, sessionId: string, sessionVersion?: number | string) => {
      e.stopPropagation();
      if (refiringId) return;
      setRefiringId(sessionId);
      try {
        await refireSession(sessionId, "all", sessionVersion);
        notify.success("Refire sent");
        query.refetch();
      } catch (err: any) {
        notify.error(err?.message ?? "Refire failed");
      } finally {
        setRefiringId(null);
      }
    },
    [refiringId, notify, query],
  );

  const handleSearchChange = useCallback(
    (searchText: string) => {
      setSearchTerm(searchText);
      pagination.setPage(0);
    },
    [pagination],
  );

  const openAdvanced = useCallback(() => {
    setAdvancedDraft(
      advancedIndices ? Object.entries(advancedIndices).map(([indexKey, value]) => ({ indexKey, value })) : [],
    );
    setAdvancedOpen(true);
  }, [advancedIndices]);

  const handleAdvancedApply = useCallback(() => {
    const obj = Object.fromEntries(
      advancedDraft.filter((r) => r.indexKey && r.value).map((r) => [r.indexKey, r.value]),
    );
    setAdvancedIndices(obj);
    setSearchTerm("");
    setAdvancedOpen(false);
    pagination.setPage(0);
  }, [advancedDraft, pagination]);

  const handleAdvancedRevert = useCallback(() => {
    setAdvancedIndices(null);
    setAdvancedOpen(false);
    pagination.setPage(0);
  }, [pagination]);

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && e.target instanceof HTMLInputElement) {
        setAdvancedIndices(null);
        setAdvancedOpen(false);
        pagination.setPage(0);
      }
    },
    [pagination],
  );

  const baseSessionColumn: DataTableColumn = {
    field: "id",
    headerName: "Session",
    minWidth: 116,
    width: 126,
    maxWidth: 140,
    renderCell: (params) => {
      const sessionId = params.row.id ?? params.row._id;
      return (
        <ShortID
          value={sessionId}
          prefix="#"
          suffix="..."
          className="whitespace-nowrap text-ellipsis"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            navigator.clipboard.writeText(sessionId);
            notify.success("Session ID copied to clipboard");
          }}
          variant="h6"
        />
      );
    },
  };

  const releaseColumn: DataTableColumn = {
    field: "release",
    headerName: "Rel",
    minWidth: 64,
    width: 72,
    maxWidth: 84,
    renderCell: (params) => {
      const releaseNo = params.row.releaseNo;
      return releaseNo !== undefined && releaseNo !== null ? String(releaseNo) : "-";
    },
  };

  const detailToggleColumn: DataTableColumn = {
    field: DETAIL_PANEL_TOGGLE_FIELD,
    headerName: "",
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    width: 56,
    align: "center",
    headerAlign: "center",
    resizable: false,
    renderCell: (params) => {
      const expanded = expandedSessionRowIds.has(params.id);
      return (
        <Button
          icon
          size="sm"
          label={expanded ? "Collapse details" : "Expand details"}
          onClick={(event) => {
            event.stopPropagation();
            (params.api as any).toggleDetailPanel(params.id);
          }}
        >
          <Icon name={expanded ? "expand" : "go-forward"} />
        </Button>
      );
    },
  };

  const failedCount = (data: any) => data.failedInteractions?.length ?? 0;
  const completeCount = (data: any) =>
    (data.totalInteractions ?? 0) - (data.totalActiveInteractions ?? 0) - failedCount(data);

  const columns = useMemo((): DataTableColumn[] => {
    if (showFailedState) {
      return [
        detailToggleColumn,
        baseSessionColumn,
        ...(showReleaseColumn ? [releaseColumn] : []),
        {
          field: "lastFailedAt",
          headerName: "Last Failed",
          renderCell: (params) =>
            params.row.lastFailedAt ? (
              <Tooltip>
                <Tooltip.Trigger asChild>
                  <span>{moment(params.row.lastFailedAt).fromNow()}</span>
                </Tooltip.Trigger>
                <Tooltip.Content>{moment(params.row.lastFailedAt).format("YYYY-MM-DD HH:mm:ss")}</Tooltip.Content>
              </Tooltip>
            ) : (
              "—"
            ),
          minWidth: 120,
        },
        {
          field: "failedInteractions",
          headerName: "Interactions",
          minWidth: 220,
          renderCell: (params) => (
            <div className="flex items-center gap-2 flex-wrap">
              <Tooltip>
                <Tooltip.Trigger asChild>
                  <span>
                    <Chip
                      size="sm"
                      style={{ backgroundColor: "var(--destructive)", color: "var(--destructive-foreground)" }}
                    >
                      <Chip.Label>{failedCount(params.row)}</Chip.Label>
                    </Chip>
                  </span>
                </Tooltip.Trigger>
                <Tooltip.Content>Failed</Tooltip.Content>
              </Tooltip>
              <Tooltip>
                <Tooltip.Trigger asChild>
                  <span>
                    <Chip
                      size="sm"
                      style={{ color: "var(--muted-foreground)" }}
                    >
                      <Chip.Label>{params.row.totalInteractions ?? 0}</Chip.Label>
                    </Chip>
                  </span>
                </Tooltip.Trigger>
                <Tooltip.Content>Total</Tooltip.Content>
              </Tooltip>
              <Tooltip>
                <Tooltip.Trigger asChild>
                  <span>
                    <Chip
                      size="sm"
                      variant="info"
                    >
                      <Chip.Label>{Math.max(0, completeCount(params.row))}</Chip.Label>
                    </Chip>
                  </span>
                </Tooltip.Trigger>
                <Tooltip.Content>Complete</Tooltip.Content>
              </Tooltip>
            </div>
          ),
        },
        {
          field: "indices",
          headerName: "Indices",
          minWidth: showReleaseColumn ? 180 : 220,
          flex: 1,
          renderCell: (params) => <IndicesCell indices={params.row.indices} />,
        },
        {
          field: "actions",
          headerName: "",
          minWidth: 160,
          renderCell: (params) => (
            <Button
              size="sm"
              variant="contained"
              intent="secondary"
              disabled={refiringId === (params.row.id ?? params.row._id) || failedCount(params.row) === 0}
              onClick={(e) => handleRefireAll(e, params.row.id ?? params.row._id, params.row.version)}
              label={refiringId === (params.row.id ?? params.row._id) ? "Refiring…" : "Refire All Failed"}
            >
              {refiringId === (params.row.id ?? params.row._id) ? "Refiring…" : "Refire All Failed"}
            </Button>
          ),
        },
      ];
    }
    return [
      detailToggleColumn,
      baseSessionColumn,
      ...(showReleaseColumn ? [releaseColumn] : []),
      //{ title: 'Created', field: 'created', render: DateCell('created'), minWidth: 130 },
      {
        field: "lastModified",
        headerName: "Last updated",
        minWidth: 130,
        renderCell: (params) => (
          <Tooltip>
            <Tooltip.Trigger asChild>
              <span>{moment(params.row.lastModified).fromNow()}</span>
            </Tooltip.Trigger>
            <Tooltip.Content>{`Created: ${params.row.created}`}</Tooltip.Content>
          </Tooltip>
        ),
      },
      {
        field: "lastInteractionName",
        headerName: "Interactions",
        minWidth: 120,
        renderCell: (params) => (
          <div className="flex items-center gap-2">
            <Tooltip>
              <Tooltip.Trigger asChild>
                <span>
                  <Chip size="sm">
                    <Chip.Label>{params.row.totalInteractions}</Chip.Label>
                  </Chip>
                </span>
              </Tooltip.Trigger>
              <Tooltip.Content>Total Interactions</Tooltip.Content>
            </Tooltip>
            <Tooltip>
              <Tooltip.Trigger asChild>
                <span>
                  <Chip
                    size="sm"
                    variant="info"
                  >
                    <Chip.Label>{params.row.totalActiveInteractions}</Chip.Label>
                  </Chip>
                </span>
              </Tooltip.Trigger>
              <Tooltip.Content>Total Active Interactions</Tooltip.Content>
            </Tooltip>
          </div>
        ),
      },
      {
        field: "goal",
        headerName: "Goal",
        minWidth: 300,
        flex: 1,
        renderCell: (params) => {
          if (!params.row.goal) return "No goal";
          if (params.row.goalDescription) {
            return <TruncatedField value={String(params.row.goalDescription)} />;
          }
          if (!release?.rule_graph) return "";
          const releaseGraph = DecisionGraph.from(release.rule_graph);
          const goal = params.row.goal.includes("/") ? params.row.goal.split("/").pop() : params.row.goal;
          const goalId = releaseGraph.ref(goal);
          if (goalId !== undefined && releaseGraph.isListed(goalId)) {
            return <TruncatedField value={(releaseGraph.field(goalId, "description") ?? releaseGraph.field(goalId, "id")) as string} />;
          }
          return <MissingGoalField goal={String(params.row.goal)} />;
        },
      },
      {
        field: "indices",
        headerName: "Indices",
        minWidth: showReleaseColumn ? 220 : 280,
        flex: 1,
        autoSize: true,
        renderCell: (params) => <IndicesCell indices={params.row.indices} />,
      },
      {
        field: "status",
        headerName: "Status",
        minWidth: 130,
        renderCell: (params) => (
          <StatusCell
            value={params.row.status}
            color="purpleSlate"
          />
        ),
      },
    ];
  }, [showFailedState, theme, refiringId, notify, release, handleRefireAll, showReleaseColumn, expandedSessionRowIds]);

  const hasNextSessionPage =
    sessions.length >= pagination.pagination.perPage &&
    (lastAvailablePage === null || pagination.pagination.page < lastAvailablePage);

  return (
    <TableContainer $showFailedState={showFailedState}>
      <MonitorTableToolbar
        title="Sessions"
        actions={
          <>
            {releaseFilterKey ? (
              <ReleaseFilterSelect
                filterKey={releaseFilterKey}
                releaseId={releaseFilter}
                className="w-48"
                onReleaseChange={(nextReleaseId, nextReleaseLabel) => {
                  setReleaseFilter(nextReleaseId, nextReleaseLabel);
                  pagination.setPage(0);
                }}
              />
            ) : null}
            <TextInput
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              className="w-60"
              startAdornment={<Icon name="search" />}
            />
            <Button
              icon
              size="sm"
              onClick={openAdvanced}
              label="Advanced search"
              style={advancedIndices !== null ? { backgroundColor: "var(--primary)" } : undefined}
            >
              <Icon
                name="filter-list"
                style={advancedIndices !== null ? { color: "var(--primary-foreground)" } : undefined}
                fontSize="small"
              />
            </Button>
            <Button
              icon
              size="sm"
              label="Refresh"
              onClick={() => query.refetch()}
            >
              <Icon name="refresh" />
            </Button>
          </>
        }
      />
      <div className="flex flex-1 min-h-0 overflow-hidden">
        <DataTable
          autosizeKey={sessionsAutosizeKey}
          rows={sessions}
          columns={columns}
          loading={query.isLoading || query.isFetching}
          getRowId={(row) => row.id ?? row._id}
          paginationMeta={{ hasNextPage: hasNextSessionPage }}
          pagination
          paginationMode="server"
          sortingMode="server"
          detailPanelExpandedRowIds={expandedSessionRowIds}
          onDetailPanelExpandedRowIdsChange={setExpandedSessionRowIds}
          pageSizeOptions={[20, 50, 100]}
          paginationModel={{
            page: pagination.pagination.page,
            pageSize: pagination.pagination.perPage,
          }}
          onPaginationModelChange={(model) => {
            pagination.setPagination({ page: model.page, perPage: model.pageSize });
          }}
          disableColumnMenu
          getDetailPanelContent={(params) => (
            <InteractionTable
              sessionId={params.row.id ?? params.row._id}
              releaseId={params.row.release}
            />
          )}
          getDetailPanelHeight={() => 320}
          onRowClick={(params) => onSessionClick(params.row)}
        />
      </div>
      <Dialog
        open={advancedOpen}
        onOpenChange={setAdvancedOpen}
      >
        <Dialog.Content className="max-w-2xl w-[42rem]">
          <Dialog.Header>
            <Dialog.Title>Advanced search</Dialog.Title>
            <Dialog.Description>
              You can use <strong>*</strong> as a wildcard in values for backend searching.
            </Dialog.Description>
            <DialogControls allowResize={false} />
          </Dialog.Header>
          <Dialog.Body>
            <section>
              <h3 className="text-sm font-semibold mb-2">Indices</h3>
              <div className="flex flex-col gap-2">
                {advancedDraft.map((row, idx) => {
                  const usedKeys = advancedDraft.filter((_, i) => i !== idx).map((d) => d.indexKey);
                  const indexOptions: SelectOption[] = availableIndexOptions.filter(
                    (opt) => opt.value === row.indexKey || !usedKeys.includes(opt.value),
                  );
                  return (
                    <div
                      key={idx}
                      className="flex items-center gap-2"
                    >
                      <Select
                        options={indexOptions}
                        value={row.indexKey || null}
                        onValueChange={(v) =>
                          setAdvancedDraft((prev) => {
                            const n = [...prev];
                            n[idx] = { ...n[idx], indexKey: v ?? "" };
                            return n;
                          })
                        }
                        placeholder="Index"
                        className="min-w-[140px] flex-1"
                      />
                      <TextInput
                        placeholder="Value"
                        value={row.value}
                        onChange={(e) =>
                          setAdvancedDraft((prev) => {
                            const n = [...prev];
                            n[idx] = { ...n[idx], value: e.target.value };
                            return n;
                          })
                        }
                        className="flex-1 min-h-9"
                        fullWidth
                      />
                      <Button
                        icon
                        size="sm"
                        label="Remove"
                        onClick={() => setAdvancedDraft((prev) => prev.filter((_, i) => i !== idx))}
                      >
                        <Icon name="delete" />
                      </Button>
                    </div>
                  );
                })}
                <Button
                  variant="contained"
                  intent="secondary"
                  onClick={() => setAdvancedDraft((prev) => [...prev, { indexKey: "", value: "" }])}
                  label="Add index"
                />
              </div>
            </section>
          </Dialog.Body>
          <Dialog.Actions>
            <Dialog.Actions.SecondaryButton
              onClick={handleAdvancedRevert}
              label="Revert"
            >
              Revert
            </Dialog.Actions.SecondaryButton>
            <Dialog.Actions.PrimaryButton
              onClick={handleAdvancedApply}
              label="Apply"
            >
              Apply
            </Dialog.Actions.PrimaryButton>
          </Dialog.Actions>
        </Dialog.Content>
      </Dialog>
    </TableContainer>
  );
};
